import React from "react";
import { Select, Input as AntInput } from "antd";
import { ChevronDown } from "lucide-react";
import classnames from "classnames";
import "./SelectCompactAntd.css";

export function SelectCompactAntd({
  value,
  onChange,
  options = [],
  className,
  popupClassName,
  width = "w-full",
  size = "small",
  inputSize = "small",
  style = {},
  placeholder = "Select...",
  mode,
  label,
  ...props
}) {
  const isMultiple = mode === "multiple";
  const isMd = inputSize === "md";
  const defaultStyle = {
    fontSize: isMd ? "12px" : isMultiple ? "13px" : "10px",
    ...style,
  };

  const heightCls = isMd
    ? "!h-[32px] [&_.ant-select-selector]:!h-[32px] [&_.ant-select-selector]:!min-h-[32px] [&_.ant-select-single]:!h-[32px] [&_.ant-select-single_.ant-select-selector]:!h-[32px] [&_.ant-select-single_.ant-select-selector]:!min-h-[32px]"
    : "!h-[32px] [&_.ant-select-selector]:!h-[32px] [&_.ant-select-selector]:!min-h-[32px] [&_.ant-select-single]:!h-[32px] [&_.ant-select-single_.ant-select-selector]:!h-[32px] [&_.ant-select-single_.ant-select-selector]:!min-h-[32px]";

  const widthClasses =
    width === "w-full"
      ? isMultiple
        ? "!w-full [&_.ant-select-selector]:!bg-white [&_.ant-select-selector]:!rounded-md [&_.ant-select-selector]:!min-h-[36px] [&_.ant-select-selector]:!h-auto"
        : "!w-full [&_.ant-select-selector]:!bg-white [&_.ant-select-selector]:!rounded-md"
      : width === "w-24"
      ? "!w-24"
      : `!${width}`;

  const defaultClassName = classnames(
    "grigora-select-compact",
    widthClasses,
    heightCls,
    "[&_.ant-select-single_.ant-select-selector]:!py-1 [&_.ant-select-single_.ant-select-selector]:!items-center",
    "[&_.ant-select-selector]:!text-left [&_.ant-select-selector]:!bg-white [&_.ant-select-selector]:!border-slate-200 [&_.ant-select-selector]:!rounded [&_.ant-select-selector:hover]:!border-slate-300 [&_.ant-select-selector:focus]:!border-[#623CE7]",
    "[&_.ant-select-focused_.ant-select-selector]:!border-[#623CE7] [&_.ant-select-focused_.ant-select-selector]:!bg-white [&_.ant-select-focused_.ant-select-selector]:!shadow-[0_0_0_2px_rgba(98,60,231,0.1)]",
    "[&_.ant-select-selection-item]:!text-left [&_.ant-select-selection-item]:!text-slate-700 [&_.ant-select-selection-placeholder]:!text-slate-400",
    "[&_.ant-select-selector]:!flex [&_.ant-select-selector]:!items-center [&_.ant-select-selector]:!relative [&_.ant-select-selector]:!pr-6 [&_.ant-select-selector]:!min-w-0",
    "[&_.ant-select-selection-item]:!flex-1 [&_.ant-select-selection-item]:!min-w-0 [&_.ant-select-selection-placeholder]:!flex-1 [&_.ant-select-selection-placeholder]:!min-w-0",
    "[&_.ant-select-arrow]:!absolute [&_.ant-select-arrow]:!right-2 [&_.ant-select-arrow]:!m-0 [&_.ant-select-arrow]:!p-0 [&_.ant-select-arrow]:!h-full [&_.ant-select-arrow]:!flex [&_.ant-select-arrow]:!items-center [&_.ant-select-arrow]:!justify-center",
    "[&_.ant-select-arrow_*]:!m-0 [&_.ant-select-arrow_*]:!p-0 [&_.ant-select-arrow_*]:!block",
    className,
  );

  const mappedOptions = options.map((opt) => ({
    label: opt.label,
    value: opt.value === null || opt.value === undefined ? undefined : opt.value,
  }));

  const displayValue = value === null || value === undefined ? undefined : value;

  const handleChange = (val) => {
    if (onChange) {
      if (isMultiple) {
        onChange(Array.isArray(val) ? val : []);
      } else {
        onChange(val === null || val === undefined ? "" : val);
      }
    }
  };

  return (
    <div>
      {label && (
        <label className="block text-slate-700 text-xs font-semibold mb-1.5 capitalize">
          {label}
        </label>
      )}
      <Select
        value={displayValue}
        onChange={handleChange}
        mode={mode}
        className={defaultClassName}
        size={size}
        style={defaultStyle}
        placeholder={placeholder}
        options={mappedOptions}
        popupClassName={classnames("grigora-select-compact-dropdown", popupClassName)}
        dropdownClassName={classnames("grigora-select-compact-dropdown", popupClassName)}
        suffixIcon={
          <ChevronDown
            size={10}
            className="text-slate-400"
            style={{ margin: 0, padding: 0, display: "block", lineHeight: 1 }}
          />
        }
        getPopupContainer={(triggerNode) => triggerNode?.parentElement || document.body}
        {...props}
      />
    </div>
  );
}

/** Ant Design Input – matches Select height (36px), border, radius for Store Listing. */
export function InputCompactAntd({ label, value, onChange, placeholder, className, ...props }) {
  return (
    <div className={classnames("input-compact-antd-wrap", className)}>
      {label && (
        <label className="block text-slate-700 text-xs font-semibold mb-1.5 capitalize">
          {label}
        </label>
      )}
      <AntInput
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        size="small"
        className="grigora-input-compact"
        {...props}
      />
    </div>
  );
}
